import { ArrowUpDownIcon, ChevronDownIcon } from 'lucide-react'

import { Button } from '../../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu'

export interface SortOption<T extends string> {
  value: T
  label: string
}

interface SortMenuProps<T extends string> {
  value: T
  onChange: (next: T) => void
  options: ReadonlyArray<SortOption<T>>
  className?: string
}

/**
 * Compact sort selector — radio-style dropdown that shows the active
 * option as a short trailing label so an operator can read the current
 * order without opening the menu. Shared across the Configure list tabs.
 */
export function SortMenu<T extends string>({
  value,
  onChange,
  options,
  className
}: SortMenuProps<T>) {
  const active = options.find((o) => o.value === value)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`h-8 gap-1.5 px-2.5 text-xs font-medium text-ink-2 ${className ?? ''}`}
        >
          <ArrowUpDownIcon className="size-3.5 text-ink-4" aria-hidden />
          <span>Sort:</span>
          <span className="text-ink">{active?.label ?? value}</span>
          <ChevronDownIcon className="size-3 text-ink-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem] p-0.5">
        <DropdownMenuRadioGroup value={value} onValueChange={(v) => onChange(v as T)}>
          {options.map((opt) => (
            <DropdownMenuRadioItem
              key={opt.value}
              value={opt.value}
              className="cursor-pointer py-1 pr-2 pl-7 text-xs text-ink-2 [&_svg]:size-3 [&>span:first-child]:left-1.5 [&>span:first-child]:size-3"
            >
              {opt.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
